import React, { useState, useCallback, useMemo, useEffect } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { Formik, Form, FastField, Field, FormikHelpers, useFormikContext } from "formik";
import * as Yup from "yup";
import { Tooltip } from "@mui/material";
import { useGlobalState } from "../../context/useGlobalState";
import { updateActivity } from "../../api/activity";
import Button from "../../components/ui/Button";
import { FileInput } from "../../components/common/FileInput";
import { uploadFileToS3Complete } from "../../api/s3";
import ImageList from "../../components/images/ImageList";
import { TextField, DropdownField, NumberField, RadioField } from "../../components/common/FormFields";
import FormGroup from "../../components/common/FormGroup";
import ImageSpecsBadges from "../../components/common/ImageSpecsBadges";
import { useImageValidation } from "../../hooks/useImageValidation";
import { IMAGE_FORMAT, IMAGE_MAX_SIZE_200_KB } from "../../utils/image";
import { ActivityDTO } from "../../types/requestsOutputs";
import logo from "../../assets/images/logo-rewind.png";
import { isVdpCmsHost } from "../../utils/cmsHost";
import { useLanguage } from "../../context/LanguageContext";
import ActivityTypeField from "./ActivityTypeField";
import activeRadio from "../../assets/images/radio_active.png";
import inactiveRadio from "../../assets/images/radio_inactive.png";
import styles from "./EditActivityModal.module.css";
import stepStyles from "../track/create/createStep.module.css";

// Types
interface ActivityFormValues {
    isOnboard: boolean;
    coverImage: string;
    duration: number;
    position: number;
    price: number;
    promotionPercentage: number;
    coordinates: string; // e.g. "48.8566, 2.3522"
    name: string;
    url: string;
    contentLanguage: string;
    cityId: string;
    activityPCVTypeId: string;
}

interface EditActivityModalProps {
    isOpen: boolean;
    onClose: () => void;
    activity: ActivityDTO;
    onSuccess: () => void;
}

type LocalizedRow = { id?: string; language: string; name: string; url: string };

function normalizeLangCode(code: string | undefined): string {
    if (!code) return "";
    const part = code.trim().split(/[-_]/)[0];
    return part.length >= 2 ? part.slice(0, 2).toUpperCase() : part.toUpperCase();
}

function toNumberOrZero(value: unknown): number {
    const n = typeof value === "number" ? value : Number(value);
    return Number.isFinite(n) ? n : 0;
}

function getActivityLocalizedRows(activity: ActivityDTO): LocalizedRow[] {
    const raw = activity.activityPCVLocalized;
    if (Array.isArray(raw)) {
        return raw.map((l) => ({
            id: l.id,
            language: l.language || "",
            name: l.name || "",
            url: l.url || "",
        }));
    }
    if (raw && typeof raw === "object") {
        const o = raw as { id?: string; language?: string; name?: string; url?: string };
        return [{ id: o.id, language: o.language || "", name: o.name || "", url: o.url || "" }];
    }
    return [];
}

/** Prefer CMS UI language, then FR / EN, else first row (API order is not stable). */
function pickLocalizedForEdit(activity: ActivityDTO, cmsUiLanguage: string): LocalizedRow {
    const rows = getActivityLocalizedRows(activity);
    const wanted = normalizeLangCode(cmsUiLanguage || "EN");

    const matchWanted = rows.find((r) => normalizeLangCode(r.language) === wanted);
    if (matchWanted) return matchWanted;

    const matchFr = rows.find((r) => normalizeLangCode(r.language) === "FR");
    if (matchFr) return matchFr;

    const matchEn = rows.find((r) => normalizeLangCode(r.language) === "EN");
    if (matchEn) return matchEn;

    if (rows.length > 0) return rows[0];

    return {
        name: activity.name || "",
        url: activity.url || "",
        language: wanted || "EN",
        id: undefined,
    };
}

const EditActivityModal: React.FC<EditActivityModalProps> = ({ isOpen, onClose, activity, onSuccess }) => {
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;
    const { language: cmsUiLanguage } = useLanguage();
    const { data } = useGlobalState();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const showOnboardField = useMemo(() => isVdpCmsHost(), []);

    if (!isOpen) return null;

    const selectedLocalized = pickLocalizedForEdit(activity, cmsUiLanguage);

    // Map language code to language ID for dropdown
    // The dropdown uses language.id as value, but activity stores language.code
    const getLanguageIdFromCode = (languageCode: string): string => {
        if (!languageCode || !data?.languagesAI) return "";
        const norm = normalizeLangCode(languageCode);
        const langByCode = data.languagesAI.find(
            (lang: any) => normalizeLangCode(lang.code) === norm || String(lang.code).toUpperCase() === norm,
        );
        if (langByCode) return langByCode.value;
        const langById = data.languagesAI.find((lang: any) => lang.value === languageCode);
        if (langById) return langById.value;
        return data.languagesAI[0]?.value || "";
    };

    const validationSchema = Yup.object({
        isOnboard: Yup.boolean().required(t("requiredIsOnboard")),
        coverImage: Yup.string().required(t("requiredCoverImage")),
        duration: Yup.number().min(0),
        position: Yup.number().min(0),
        price: Yup.number().min(0),
        promotionPercentage: Yup.number().min(0).max(100),
        coordinates: Yup.string()
            .matches(/^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/, t("invalidCoordinatesFormat"))
            .test("is-valid-coordinates", t("invalidCoordinates"), (value) => {
                if (!value) return true; // Optional field
                const [lat, lng] = value.split(",").map((v) => parseFloat(v.trim()));
                return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
            }),
        name: Yup.string().required(t("requiredName")),
        url: Yup.string().required(t("requiredUrl")).url(t("invalidUrl")),
        contentLanguage: Yup.string().required(t("requiredContentLanguage")),
        cityId: Yup.string().required(t("requiredCityId")),
        activityPCVTypeId: Yup.string().required(t("requiredActivityType")),
    });

    const initialValues: ActivityFormValues = {
        isOnboard: showOnboardField ? activity.isOnboard || false : false,
        coverImage: activity.coverImage || "",
        duration: toNumberOrZero(activity.duration),
        position: toNumberOrZero(activity.position),
        price: toNumberOrZero(activity.price),
        promotionPercentage: toNumberOrZero(activity.promotionPercentage),
        coordinates: activity.latitude && activity.longitude ? `${activity.latitude}, ${activity.longitude}` : "",
        name: selectedLocalized.name || activity.name || "",
        url: selectedLocalized.url || activity.url || "",
        contentLanguage: getLanguageIdFromCode(selectedLocalized.language || cmsUiLanguage || "EN"),
        cityId: activity.cityId || "",
        activityPCVTypeId: activity.activityPCVTypeId || "",
    };

    const handleSubmit = async (values: ActivityFormValues, { setErrors }: FormikHelpers<ActivityFormValues>) => {
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("Missing token");
            return;
        }

        try {
            setIsSubmitting(true);
            let lat = 0;
            let lng = 0;
            if (values.coordinates && values.coordinates.trim()) {
                [lat, lng] = values.coordinates.split(",").map((val) => parseFloat(val.trim()));
            }

            const payload = {
                isOnboard: showOnboardField ? values.isOnboard : false,
                coverImage: values.coverImage || "",
                duration: Number(values.duration) || 0,
                position: Number(values.position) || 0,
                price: Number(values.price) || 0,
                promotionPercentage: Number(values.promotionPercentage) || 0,
                latitude: lat,
                longitude: lng,
                cityId: values.cityId,
                activityPCVTypeId: values.activityPCVTypeId || "",
                localized: [
                    {
                        id: selectedLocalized.id,
                        language: values.contentLanguage || "EN",
                        name: values.name,
                        url: values.url,
                    },
                ],
            };

            await updateActivity({
                token,
                id: activity.activityPCVId || activity.id,
                payload,
            });

            onSuccess();
        } catch (err: any) {
            console.error("Error updating activity:", err);
            if (err.response?.data?.errors) {
                setErrors(err.response.data.errors);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const ActivityFormContent = () => {
        const { values, setFieldValue } = useFormikContext<ActivityFormValues>();
        const { coverImage } = values;
        const imageValidation = useImageValidation({
            type: "activity",
            imageUrl: coverImage || undefined,
        });

        useEffect(() => {
            if (!showOnboardField) {
                setFieldValue("isOnboard", false);
            }
        }, [showOnboardField, setFieldValue]);

        // Location handling, aligned with notifications: city is mandatory but when
        // the company has a single city we auto-fill it and hide the picker.
        const cityOptions = useMemo(() => data?.locationCityOptions || [], [data?.locationCityOptions]);
        const hasSingleCity = cityOptions.length === 1;

        useEffect(() => {
            if (hasSingleCity && !values.cityId) {
                setFieldValue("cityId", cityOptions[0].value);
            }
        }, [hasSingleCity, cityOptions, values.cityId, setFieldValue]);

        const handleFileSelected = useCallback(
            async (files: File | File[]) => {
                const file = Array.isArray(files) ? files[0] : files;
                if (file) {
                    const token = localStorage.getItem("token");
                    if (!token) {
                        console.error("No authentication token found");
                        return;
                    }

                    await imageValidation.validateImage(file);

                    uploadFileToS3Complete(
                        {
                            file,
                            isPublic: true,
                            isFolder: false,
                        },
                        token,
                    )
                        .then((result) => {
                            setFieldValue("coverImage", result);
                        })
                        .catch(console.error);
                }
            },
            [setFieldValue, imageValidation.validateImage],
        );

        const setPicturesFromList = useCallback(
            (pictures: string[]) => {
                setFieldValue("coverImage", pictures[0] || null);
            },
            [setFieldValue],
        );

        return (
            <Form style={{ width: "100%" }}>
                <h1 className={styles.sectionTitle}>{t("contentSettings")}</h1>

                <div style={{ width: "100%" }}>
                    {!hasSingleCity && (
                        <FormGroup title={t("location")} subtitle={t("activityLocationSub")} required>
                            <Field
                                name="cityId"
                                options={cityOptions}
                                placeholder={t("selectALocation")}
                                component={DropdownField}
                                fullWidth
                            />
                        </FormGroup>
                    )}

                    <ActivityTypeField title={t("activityType")} subtitle={t("activityTypeSub")} required />

                    <FormGroup
                        title={t("chooseName")}
                        subtitle={t("chooseNameSub")}
                        tooltip={t("doNotTranslateTooltip")}
                        required
                    >
                        <FastField
                            name="name"
                            label={t("name")}
                            placeholder={t("name")}
                            errorMessage={t("invalidName")}
                            component={TextField}
                        />
                    </FormGroup>

                    <FormGroup title={t("chooseUrl")} subtitle={t("chooseUrlSub")} required>
                        <FastField
                            name="url"
                            label={t("url")}
                            placeholder={t("url")}
                            errorMessage={t("invalidUrl")}
                            component={TextField}
                        />
                    </FormGroup>

                    <FormGroup title={t("Language")} subtitle={t("selectLanguageActivity")} required>
                        <Field
                            name="contentLanguage"
                            options={data.languagesAI}
                            placeholder={t("selectALanguage")}
                            component={DropdownField}
                            fullWidth
                        />
                    </FormGroup>

                    <FormGroup title={t("coordinates")} subtitle={t("enterCoordinatesLatLng")}>
                        <FastField
                            name="coordinates"
                            label={t("Coordinates")}
                            placeholder="48.8566, 2.3522"
                            errorMessage={t("Invalid coordinates")}
                            component={TextField}
                        />
                    </FormGroup>

                    <FormGroup title={t("uploadYourActivityImage")} subtitle={t("uploadYourActivityImageSub")} required>
                        <ImageSpecsBadges spec="activity" warnings={imageValidation.warnings} />
                        <div className={`${stepStyles.fileUpload} ${styles.fileUpload}`}>
                            <FileInput
                                onFileSelected={handleFileSelected}
                                showButton={true}
                                buttonVariant="outline"
                                buttonText={t("add")}
                                acceptedFileTypes={IMAGE_FORMAT}
                                maxSize={IMAGE_MAX_SIZE_200_KB}
                                maxSizeLabel={t("imageMaxSize200kb")}
                            />
                        </div>
                        {coverImage && (
                            <div className="photo-gallery" style={{ maxWidth: "100%" }}>
                                <ImageList
                                    pictures={coverImage ? [coverImage] : []}
                                    setPictures={setPicturesFromList}
                                />
                            </div>
                        )}
                    </FormGroup>

                    {showOnboardField && (
                        <FormGroup title={t("isOnBoard")} style={{ marginTop: 20 }}>
                            <Field
                                name="isOnboard"
                                component={RadioField}
                                value={true}
                                label={t("isOnBoardTrue")}
                                subtitle={t("isOnBoardTrueSubtitle")}
                                activeImage={activeRadio}
                                inactiveImage={inactiveRadio}
                            />
                            <Field
                                name="isOnboard"
                                component={RadioField}
                                value={false}
                                label={t("isOnBoardFalse")}
                                subtitle={t("isOnBoardFalseSubtitle")}
                                activeImage={activeRadio}
                                inactiveImage={inactiveRadio}
                            />
                        </FormGroup>
                    )}

                    <FormGroup title={t("choosePrice")} subtitle={t("choosePriceSub")}>
                        <FastField
                            name="price"
                            label={t("price")}
                            placeholder={t("price")}
                            errorMessage={t("invalidPrice")}
                            component={TextField}
                        />
                    </FormGroup>

                    <FormGroup title={t("activityDuration")} subtitle={t("activityDurationSub")}>
                        <div className={styles.numRow}>
                            <FastField
                                name="duration"
                                label={t("duration")}
                                placeholder={t("durationPlaceholder")}
                                errorMessage={t("invalidDuration")}
                                component={NumberField}
                                min={0}
                            />
                            <span className={styles.numUnit}>min</span>
                        </div>
                    </FormGroup>

                    <FormGroup
                        title={t("activityPosition")}
                        subtitle={t("activityPositionSub")}
                        tooltip={t("activityPositionInfo")}
                    >
                        <div className={styles.numRow}>
                            <FastField
                                name="position"
                                label={t("position")}
                                placeholder={t("positionPlaceholder")}
                                errorMessage={t("invalidPosition")}
                                component={NumberField}
                                min={0}
                            />
                        </div>
                    </FormGroup>
                </div>
            </Form>
        );
    };

    const modal = (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.head}>
                    <img src={logo} alt="Rewind" className={styles.logo} />
                    <div className={styles.title}>{t("editActivity")}</div>
                </div>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    key={`${activity.activityPCVId || activity.id}-${cmsUiLanguage}-${i18n.language}`}
                    enableReinitialize
                >
                    {({ submitForm, errors }) => (
                        <>
                            <div className={styles.body}>
                                <ActivityFormContent />
                            </div>
                            <div className={styles.foot}>
                                <Button variant="cancel" label={t("cancel")} onClick={onClose} />
                                <Tooltip
                                    title={
                                        Object.keys(errors).length > 0
                                            ? t("formIncompleteTooltip", {
                                                  fields: Object.values(errors).join(", "),
                                              })
                                            : ""
                                    }
                                    arrow
                                    PopperProps={{
                                        style: {
                                            zIndex: 9999,
                                        },
                                    }}
                                >
                                    <span>
                                        <Button
                                            variant="primary"
                                            label={t("save")}
                                            onClick={submitForm}
                                            disabled={isSubmitting || Object.keys(errors).length > 0}
                                        />
                                    </span>
                                </Tooltip>
                            </div>
                        </>
                    )}
                </Formik>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

export default EditActivityModal;
